/**
 * @file A drop-in companion products controller. This is part of quick-scripts.
 * @author Trevor Richards <trevor@voltagenewmedia.com>
 * @version 1.0.0
 */

/**
 * A callback function to apply after companion products have been added to
 * the Shopify cart.
 *
 * @callback afterCompanionsAdded
 * @param {Object} res - Response object.
 */

/**
 * Optional settings for the Companion Products.
 *
 * @typedef {Object} CompanionOptions
 * @property {string} [button=".add-to-cart"] - Query selector for add-to-cart button(s).
 * @property {string} [product="input[name=id]"] - Query selector for product ids.
 * @property {string} [quantity="input[name=quantity]"] - Query selector for product quantities.
 * @property {boolean} [clickThrough=false] - Continue with default click behavior on button elements?
 */

/**
 * The expected settings for Companion Products
 *
 * @typedef {Object} CompanionSettings
 * @property {string} button - Query selector for add-to-cart button(s).
 * @property {string} product - Query selector for product ids.
 * @property {string} quantity - Query selector for product quantities.
 * @property {boolean} clickThrough - Continue with default click behavior on button elements?
 */

/**
 * Initialize the voltage companions feature.
 *
 * @param {CompanionOptions} [options] - The settings for the companion products.
 * @param {afterCompanionsAdded} [afterAdded] - Callback to be fired after items are added.
 */
function initProductCompanions(options, afterAdded) {
  /**
   * The defaults settings for options.
   *
   * @type {CompanionSettings}
   */
  var defaults = {
    button: '.add-to-cart',
    product: 'input[name="id"]',
    quantity: 'input[name="quantity"]',
    clickThrough: false
  };

  var settings = Object.assign(defaults, options);
  /**
   * Whether or not to skip the next attempt to add the companion products
   * to the cart. This is used in lexical context to handleButtonClick. This
   * variable is what makes the clickThrough option work.
   */
  var bypass = false;

  /** @type {HTMLElement|null} */
  var button = document.querySelector(settings.button);
  /** @type {HTMLSelectElement[]} */
  var selectBoxes = Array.prototype.slice.call(
    document.querySelectorAll('.companion-variant')
  );

  /**
   * Attempt to add the companion-products to the cart.
   *
   * @param {MouseEvent} evt - The click event.
   * @throws Error - Parent form is somehow not a form.
   * @throws Error - Form variant ID input could not be found.
   */
  function handleButtonClick(evt) {
    /** @type {HTMLElement|null} */
    var companions = document.querySelector('.companion-products');
    if (companions === null) return;
    if (bypass) {
      bypass = false;
      return;
    }

    evt.preventDefault();

    /** @type {HTMLInputElement|null} */
    var productInput = document.querySelector(settings.product);
    /** @type {HTMLInputElement|null} */
    var qtyInput = document.querySelector(settings.quantity);
    if (!productInput) {
      throw Error("Could not find the main product variant ID.");
    }

    /** @type {NodeListOf<HTMLInputElement>} */
    var checkedNodes = document
      .querySelectorAll('.companion-product .product-checkbox:checked');
    /** @type {HTMLInputElement[]} */
    var upsells = Array.prototype.slice.call(checkedNodes);

    /**
     * An array of products to add to the shopping cart.
     *
     * @typedef {Array.<{id: string, quantity: number}>} Payload
     */

    /** @type {Payload} */
    var initialPayload = settings.clickThrough ? [] : [{
      id: productInput.value,
      quantity: Number(qtyInput?.value || 1)
    }];

    /** @type {Payload} */
    var payload = upsells.reduce(function(items, item) {
      var id = item.dataset.product;
      if (!id) return items;
      return items.concat([{
        id,
        quantity: 1
      }]);
    }, initialPayload);

    var post = new XMLHttpRequest();
    post.onreadystatechange = function() {
      if (post.readyState === post.DONE && post.status === 200) {
        if (typeof afterAdded === "function") {
          afterAdded(JSON.parse(post.responseText));
        } else {
          if (!settings.clickThrough) {
            console.log(
              '%cCOMPANION: Products were added but no callback was called',
              'color: orange'
            );
          }
        }
      }

      if (post.readyState === post.DONE && settings.clickThrough) {
        bypass = true;
        if (button) {
          button.click();
        }
      }
    };
    post.open('POST', '/cart/add.js');
    post.setRequestHeader('Content-Type', 'application/json');
    post.send(JSON.stringify({ items: payload }));
  }

  /**
   * Update checkbox variant id on selector change.
   *
   * @param {HTMLSelectElement} box - Variant selector.
   * @throws Error - The select box is not tied to a product id.
   * @throws Error - The checkbox for adding a variant ID is missing.
   */
  function handleVariantSelector(box) {
    var product = box.dataset.product;
    if (!product) throw Error("Selector not correlated with any product.");
    /** @type {HTMLInputElement|null} */
    var checkbox = document.querySelector('#companion-' + product);
    if (!checkbox) throw Error("Checkbox missing for: " + box);
    checkbox.dataset.product = box.value;
  }

  selectBoxes.forEach(function(box) {
    box.addEventListener('change', function() {
      handleVariantSelector(box);
    });
  });

  if (!button) {
    throw Error("No click target for companion products");
  }
  button.addEventListener('click', handleButtonClick);
}

initProductCompanions({
  product: 'form.product-single__form select[name="id"]'
}, function(cart) {
  /** @type {[string, { detail: Object }|{}][]} */
  var events = [
    ['cart:build', {}],
    ['cart:updated', { detail: { cart } }],
    ['cart:open', {}]
  ];
  events.forEach(evt => {
    document.dispatchEvent(new CustomEvent(evt[0], evt[1]));
  });
});
